import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { GridPager } from "../../common/pagination/GridPager";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import TableContainer from "../../common/ui/table-container/TableContainer";
import "../documents/Documents.css";
import "../../common/ui/data-table/DataTable.css";
import "rc-pagination/assets/index.css";
import "./Attendance.css";
import {
  CUSTOM_ENROLL,
  MAPPING_ROSTER_PAGE_SIZE,
  UNMAPPED_DEFAULT_PAGE_SIZE,
  deleteDeviceMapping,
  fetchAgentInstallations,
  fetchAttendanceDevices,
  fetchMappingRoster,
  fetchMappedPunches,
  fetchUnmappedPunches,
  handleAttendanceApiError,
  reprocessUnmappedPunches,
  revokeAgentInstallation,
  updateMappingEnrollment,
} from "./attendanceMachineApi";
import { Common, WebStorage, WebStorageNames } from "../../../utils";
import { getAttendanceDisplayTimeZone, parseUtcFromApi } from "./attendanceDateUtils";
import { ATTENDANCE_AGENT_DOWNLOAD_URL } from "../../../utils/attendanceAgentUtils";

const AGENT_GUIDE_STEPS = [
  {
    titleKey: "guideStep1Title",
    bodyKey: "guideStep1Body",
    altKey: "guideStep1ImgAlt",
    image: "/assets/attendance-agent/guide/guide-same-network.png?v=2",
  },
  {
    titleKey: "guideStep2Title",
    bodyKey: "guideStep2Body",
    altKey: "guideStep2ImgAlt",
    image: "/assets/attendance-agent/guide/guide-set-machine-ip.png?v=2",
  },
  {
    titleKey: "guideStep3Title",
    bodyKey: "guideStep3Body",
    altKey: "guideStep3ImgAlt",
    image: "/assets/attendance-agent/guide/guide-enter-ip-in-agent.png?v=2",
  },
];

const getRoleId = () => {
  const r = WebStorage.getLocalStore(WebStorageNames.RoleId) ?? localStorage.getItem("roleId");
  const n = parseInt(String(r), 10);
  return Number.isFinite(n) ? n : null;
};

const isCompanyAdmin = (rid) => rid === 1 || rid === 2;

const formatWhen = (v) => {
  const d = parseUtcFromApi(v);
  if (!d || Number.isNaN(d.getTime())) return "—";
  try {
    return d.toLocaleString();
  } catch {
    return String(v);
  }
};

const formatPunchWhen = (v) => {
  const d = parseUtcFromApi(v);
  if (!d || Number.isNaN(d.getTime())) return { date: "—", time: "" };
  const timeZone = getAttendanceDisplayTimeZone();
  return {
    date: new Intl.DateTimeFormat(undefined, {
      timeZone,
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(d),
    time: new Intl.DateTimeFormat(undefined, {
      timeZone,
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
    }).format(d),
  };
};

const inOutHintMeta = (hint, t) => {
  if (hint === 1) {
    return { label: t("app.attendance.hub.checkIn"), className: "att-machines-hint att-machines-hint--in" };
  }
  if (hint === 2) {
    return { label: t("app.attendance.hub.checkOut"), className: "att-machines-hint att-machines-hint--out" };
  }
  return { label: t("app.attendance.machines.hintUnknown"), className: "att-machines-hint att-machines-hint--unknown" };
};

const TABS = [
  { key: "agents", icon: "bi-pc-display" },
  { key: "devices", icon: "bi-hdd-network" },
  { key: "mappings", icon: "bi-person-badge" },
  { key: "unmapped", icon: "bi-exclamation-diamond" },
  { key: "mapped", icon: "bi-check2-circle" },
];

const deviceStatusMeta = (device, t) => {
  if (device.isOnline) {
    return { label: t("app.attendance.machines.statusOnline"), className: "att-machines-hint att-machines-hint--in" };
  }
  if (device.lastHeartbeatAt || device.lastSyncAt) {
    return { label: t("app.attendance.machines.statusOffline"), className: "att-machines-hint att-machines-hint--out" };
  }
  return { label: t("app.attendance.machines.statusUnknown"), className: "att-machines-hint att-machines-hint--unknown" };
};

const mappedPunchStatusMeta = (status, t) => {
  if (status === 1) {
    return { label: t("app.attendance.machines.mappedApplied"), className: "att-machines-hint att-machines-hint--in" };
  }
  if (status === 3) {
    return { label: t("app.attendance.machines.mappedSkipped"), className: "att-machines-hint att-machines-hint--unknown" };
  }
  if (status === 4) {
    return { label: t("app.attendance.machines.mappedFailed"), className: "att-machines-hint att-machines-hint--out" };
  }
  return { label: t("app.attendance.machines.mappedPending"), className: "att-machines-hint att-machines-hint--unknown" };
};

const mappedPunchDirection = (row, t) => {
  if (row.eventType === 1) return inOutHintMeta(2, t);
  if (row.eventType === 0) return inOutHintMeta(1, t);
  return inOutHintMeta(row.inOutHint, t);
};

const initialsFromName = (name) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const MappingEmployeeAvatar = ({ url, name }) => {
  const [broken, setBroken] = useState(false);
  if (url && !broken) {
    return (
      <img
        src={url}
        alt=""
        className="att-cr-queue-avatar__img"
        onError={() => setBroken(true)}
      />
    );
  }
  return <span className="att-cr-queue-avatar__fallback">{initialsFromName(name)}</span>;
};

const enrollOptionsHas = (options, value) => {
  const v = String(value || "").trim();
  if (!v) return false;
  return (options || []).some((e) => e.localeCompare(v, undefined, { sensitivity: "accent" }) === 0);
};

const enrollSelectValue = (currentEnroll, draft, enrollOptions) => {
  if (draft?.select != null) return draft.select;
  if (!currentEnroll) return "";
  return enrollOptionsHas(enrollOptions, currentEnroll) ? currentEnroll : CUSTOM_ENROLL;
};

const enrollCustomValue = (currentEnroll, draft, enrollOptions) => {
  if (draft?.custom != null) return draft.custom;
  if (currentEnroll && !enrollOptionsHas(enrollOptions, currentEnroll)) return currentEnroll;
  return "";
};

const resolveEnrollDraft = (currentEnroll, draft, enrollOptions) => {
  const select = enrollSelectValue(currentEnroll, draft, enrollOptions);
  if (select === CUSTOM_ENROLL) return String(enrollCustomValue(currentEnroll, draft, enrollOptions) || "").trim();
  return String(select || "").trim();
};

const MappingEnrollEditor = ({ currentEnroll, draft, enrollOptions, t, disabled, onChange, compact = true }) => {
  const select = enrollSelectValue(currentEnroll, draft, enrollOptions);
  const custom = enrollCustomValue(currentEnroll, draft, enrollOptions);
  const selectClass = compact
    ? "form-select form-select-sm att-map-enroll__select"
    : "form-select att-map-enroll__select";
  const customClass = compact
    ? "form-control form-control-sm att-map-enroll__custom"
    : "form-control att-map-enroll__custom";
  return (
    <div className="att-map-enroll">
      <select
        className={selectClass}
        value={select}
        disabled={disabled}
        onChange={(e) => onChange({ select: e.target.value, custom })}
      >
        <option value="">{t("app.attendance.machines.enrollSelectPlaceholder")}</option>
        {(enrollOptions || []).map((enroll) => (
          <option key={enroll} value={enroll}>
            {enroll}
          </option>
        ))}
        <option value={CUSTOM_ENROLL}>{t("app.attendance.machines.enrollCustomOption")}</option>
      </select>
      {select === CUSTOM_ENROLL ? (
        <input
          className={customClass}
          value={custom}
          disabled={disabled}
          placeholder={t("app.attendance.machines.enrollCustomPlaceholder")}
          onChange={(e) => onChange({ select: CUSTOM_ENROLL, custom: e.target.value })}
        />
      ) : null}
    </div>
  );
};

const AttendanceMachinesPage = () => {
  const { t } = useTranslation();
  const roleId = getRoleId();
  const allowed = isCompanyAdmin(roleId);

  const [tab, setTab] = useState("agents");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [installations, setInstallations] = useState([]);
  const [devices, setDevices] = useState([]);
  const [unmapped, setUnmapped] = useState([]);
  const [unmappedTotal, setUnmappedTotal] = useState(0);
  const [unmappedPage, setUnmappedPage] = useState(1);
  const [unmappedPageSize, setUnmappedPageSize] = useState(UNMAPPED_DEFAULT_PAGE_SIZE);
  const [unmappedLoading, setUnmappedLoading] = useState(false);
  const [mapped, setMapped] = useState([]);
  const [mappedTotal, setMappedTotal] = useState(0);
  const [mappedPage, setMappedPage] = useState(1);
  const [mappedPageSize, setMappedPageSize] = useState(UNMAPPED_DEFAULT_PAGE_SIZE);
  const [mappedLoading, setMappedLoading] = useState(false);

  const [rosterItems, setRosterItems] = useState([]);
  const [rosterTotal, setRosterTotal] = useState(0);
  const [rosterPage, setRosterPage] = useState(1);
  const [rosterPageSize, setRosterPageSize] = useState(MAPPING_ROSTER_PAGE_SIZE);
  const [rosterUnmappedCount, setRosterUnmappedCount] = useState(0);
  const [rosterMappedCount, setRosterMappedCount] = useState(0);
  const [enrollOptions, setEnrollOptions] = useState([]);
  const [unclaimedEnrolls, setUnclaimedEnrolls] = useState([]);
  const [unmappedEmployees, setUnmappedEmployees] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [mapSearchInput, setMapSearchInput] = useState("");
  const [mapSearch, setMapSearch] = useState("");
  const [mapStatus, setMapStatus] = useState("unmapped");
  const [enrollDrafts, setEnrollDrafts] = useState({});
  const [leftoverEnroll, setLeftoverEnroll] = useState("");
  const [leftoverCustom, setLeftoverCustom] = useState("");
  const [leftoverUserId, setLeftoverUserId] = useState("");
  const [leftoverDeviceId, setLeftoverDeviceId] = useState("");
  const [mappingToRemove, setMappingToRemove] = useState(null);
  const [reprocessOpen, setReprocessOpen] = useState(false);

  const leftoverDraft = useMemo(
    () => ({ select: leftoverEnroll, custom: leftoverCustom }),
    [leftoverEnroll, leftoverCustom]
  );

  const loadUnmapped = useCallback(
    async (page = 1) => {
      if (!allowed) return;
      setUnmappedLoading(true);
      try {
        const result = await fetchUnmappedPunches({ page, pageSize: UNMAPPED_DEFAULT_PAGE_SIZE });
        setUnmapped(result.items);
        setUnmappedTotal(result.totalCount);
        setUnmappedPage(result.page);
        setUnmappedPageSize(result.pageSize);
      } catch (e) {
        handleAttendanceApiError(e, t("app.attendance.machines.loadError"));
      } finally {
        setUnmappedLoading(false);
      }
    },
    [allowed, t]
  );

  const loadMapped = useCallback(
    async (page = 1) => {
      if (!allowed) return;
      setMappedLoading(true);
      try {
        const result = await fetchMappedPunches({ page, pageSize: UNMAPPED_DEFAULT_PAGE_SIZE });
        setMapped(result.items);
        setMappedTotal(result.totalCount);
        setMappedPage(result.page);
        setMappedPageSize(result.pageSize);
      } catch (e) {
        handleAttendanceApiError(e, t("app.attendance.machines.loadError"));
      } finally {
        setMappedLoading(false);
      }
    },
    [allowed, t]
  );

  const loadRoster = useCallback(
    async (page = 1, search = "", status = "unmapped") => {
      if (!allowed) return;
      setRosterLoading(true);
      try {
        const result = await fetchMappingRoster({
          page,
          pageSize: MAPPING_ROSTER_PAGE_SIZE,
          search,
          status,
        });
        setRosterItems(result.items);
        setRosterTotal(result.totalCount);
        setRosterPage(result.page);
        setRosterPageSize(result.pageSize);
        setRosterUnmappedCount(result.unmappedCount);
        setRosterMappedCount(result.mappedCount);
        setEnrollOptions(result.enrollOptions);
        setUnclaimedEnrolls(result.unclaimedEnrollOptions);
        setUnmappedEmployees(result.unmappedEmployees);
        setEnrollDrafts({});
      } catch (e) {
        handleAttendanceApiError(e, t("app.attendance.machines.loadError"));
      } finally {
        setRosterLoading(false);
      }
    },
    [allowed, t]
  );

  const load = useCallback(async () => {
    if (!allowed) return;
    setLoading(true);
    try {
      const [inst, raw, mappedRaw, dev] = await Promise.all([
        fetchAgentInstallations().catch(() => []),
        fetchUnmappedPunches({ page: 1, pageSize: UNMAPPED_DEFAULT_PAGE_SIZE }).catch(() => ({
          items: [],
          totalCount: 0,
          page: 1,
          pageSize: UNMAPPED_DEFAULT_PAGE_SIZE,
        })),
        fetchMappedPunches({ page: 1, pageSize: UNMAPPED_DEFAULT_PAGE_SIZE }).catch(() => ({
          items: [],
          totalCount: 0,
          page: 1,
          pageSize: UNMAPPED_DEFAULT_PAGE_SIZE,
        })),
        fetchAttendanceDevices().catch(() => []),
      ]);
      setInstallations(inst);
      setUnmapped(raw.items);
      setUnmappedTotal(raw.totalCount);
      setUnmappedPage(raw.page);
      setUnmappedPageSize(raw.pageSize);
      setMapped(mappedRaw.items);
      setMappedTotal(mappedRaw.totalCount);
      setMappedPage(mappedRaw.page);
      setMappedPageSize(mappedRaw.pageSize);
      setDevices(dev);
    } catch (e) {
      handleAttendanceApiError(e, t("app.attendance.machines.loadError"));
    } finally {
      setLoading(false);
    }
  }, [allowed, t]);

  useEffect(() => {
    load();
  }, [load]);


  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = mapSearchInput.trim();
      setMapSearch((prev) => (prev === next ? prev : next));
      setRosterPage((prev) => (next === mapSearch ? prev : 1));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [mapSearchInput, mapSearch]);

  useEffect(() => {
    loadRoster(rosterPage, mapSearch, mapStatus);
  }, [loadRoster, rosterPage, mapSearch, mapStatus]);

  const activeInstallations = useMemo(() => installations.filter((x) => !x.isRevoked), [installations]);
  const revokedInstallations = useMemo(() => installations.filter((x) => x.isRevoked), [installations]);

  const onRevoke = async (id, name) => {
    if (!window.confirm(t("app.attendance.machines.revokeConfirm", { name: name || id }))) return;
    setBusy(true);
    try {
      await revokeAgentInstallation(id);
      Common.showSuccessMessage(t("app.attendance.machines.revokeSuccess"));
      await load();
    } catch (e) {
      handleAttendanceApiError(e, t("app.attendance.machines.revokeError"));
    } finally {
      setBusy(false);
    }
  };

  const onSaveRowEnrollment = async (row) => {
    const enrollmentNo = resolveEnrollDraft(row.enrollmentNo, enrollDrafts[row.userId], enrollOptions);
    if (!enrollmentNo) {
      Common.showErrorMessage(t("app.attendance.machines.mappingRequired"));
      return;
    }
    setBusy(true);
    try {
      await updateMappingEnrollment({
        userId: row.userId,
        enrollmentNo,
        deviceId: row.deviceId,
      });
      Common.showSuccessMessage(t("app.attendance.machines.enrollUpdated"));
      await loadRoster(rosterPage, mapSearch, mapStatus);
    } catch (err) {
      handleAttendanceApiError(err, t("app.attendance.machines.enrollUpdateError"));
    } finally {
      setBusy(false);
    }
  };

  const onSaveLeftoverMapping = async (e) => {
    e.preventDefault();
    const enrollmentNo = resolveEnrollDraft("", leftoverDraft, unclaimedEnrolls.length ? unclaimedEnrolls : enrollOptions);
    if (!enrollmentNo || !leftoverUserId) {
      Common.showErrorMessage(t("app.attendance.machines.mappingRequired"));
      return;
    }
    setBusy(true);
    try {
      await updateMappingEnrollment({
        userId: leftoverUserId,
        enrollmentNo,
        deviceId: leftoverDeviceId.trim() || null,
      });
      Common.showSuccessMessage(t("app.attendance.machines.mappingSaved"));
      setLeftoverEnroll("");
      setLeftoverCustom("");
      setLeftoverUserId("");
      setLeftoverDeviceId("");
      await loadRoster(1, mapSearch, mapStatus);
    } catch (err) {
      handleAttendanceApiError(err, t("app.attendance.machines.mappingSaveError"));
    } finally {
      setBusy(false);
    }
  };

  const closeRemoveMapping = () => {
    if (busy) return;
    setMappingToRemove(null);
  };

  const closeReprocess = () => {
    if (busy) return;
    setReprocessOpen(false);
  };

  const confirmRemoveMapping = async () => {
    if (!mappingToRemove?.mappingId) return;
    setBusy(true);
    try {
      await deleteDeviceMapping(mappingToRemove.mappingId);
      Common.showSuccessMessage(t("app.attendance.machines.mappingDeleted"));
      setMappingToRemove(null);
      await loadRoster(rosterPage, mapSearch, mapStatus);
    } catch (err) {
      handleAttendanceApiError(err, t("app.attendance.machines.mappingDeleteError"));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!mappingToRemove && !reprocessOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape" && !busy) {
        setMappingToRemove(null);
        setReprocessOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mappingToRemove, reprocessOpen, busy]);

  const onReprocess = async () => {
    setBusy(true);
    try {
      const result = await reprocessUnmappedPunches();
      setReprocessOpen(false);
      const progressed = result.applied + result.skipped + result.failed;
      if (progressed > 0) {
        Common.showSuccessMessage(
          t("app.attendance.machines.reprocessSuccessCounts", {
            applied: result.applied,
            remaining: result.remainingUnmapped,
          })
        );
      } else {
        Common.showErrorMessage(t("app.attendance.machines.reprocessNoneApplied"));
      }
      await loadUnmapped(1);
      await loadMapped(1);
    } catch (err) {
      handleAttendanceApiError(err, t("app.attendance.machines.reprocessError"));
    } finally {
      setBusy(false);
    }
  };

  const handleMappedPageChange = (nextPage) => {
    const page = Number(nextPage) > 0 ? Number(nextPage) : 1;
    if (page === mappedPage) return;
    loadMapped(page);
  };

  const handleUnmappedPageChange = (nextPage) => {
    const page = Number(nextPage) > 0 ? Number(nextPage) : 1;
    if (page === unmappedPage) return;
    loadUnmapped(page);
  };

  const handleRosterPageChange = (nextPage) => {
    const page = Number(nextPage) > 0 ? Number(nextPage) : 1;
    if (page === rosterPage) return;
    setRosterPage(page);
  };

  const setMapFilter = (status) => {
    if (status === mapStatus) return;
    setMapStatus(status);
    setRosterPage(1);
  };

  const unmappedFrom = unmappedTotal > 0 ? (unmappedPage - 1) * unmappedPageSize + 1 : 0;
  const unmappedTo = unmappedTotal > 0 ? Math.min(unmappedPage * unmappedPageSize, unmappedTotal) : 0;
  const mappedFrom = mappedTotal > 0 ? (mappedPage - 1) * mappedPageSize + 1 : 0;
  const mappedTo = mappedTotal > 0 ? Math.min(mappedPage * mappedPageSize, mappedTotal) : 0;
  const rosterFrom = rosterTotal > 0 ? (rosterPage - 1) * rosterPageSize + 1 : 0;
  const rosterTo = rosterTotal > 0 ? Math.min(rosterPage * rosterPageSize, rosterTotal) : 0;
  const leftoverEnrollList = unclaimedEnrolls.length ? unclaimedEnrolls : enrollOptions;

  if (!allowed) {
    return (
      <div className="px-3 py-4">
        <p className="text-muted">{t("app.attendance.machines.adminOnly")}</p>
      </div>
    );
  }

  return (
    <div className="px-3 px-md-4 py-4">
      <div className="att-attendance-route">
        <CommonPageHeader
          icon={<i className="bi bi-hdd-network" />}
          title={t("app.attendance.machines.title")}
          description={t("app.attendance.machines.subtitle")}
        />

        <div className="att-page">
          <div
            className="att-machines-nav"
            role="tablist"
            aria-label={t("app.attendance.machines.title")}
          >
            {TABS.map(({ key, icon }) => {
              const count =
                key === "unmapped"
                  ? unmappedTotal
                  : key === "mapped"
                    ? mappedTotal
                    : key === "devices"
                      ? devices.length
                      : key === "mappings"
                        ? rosterUnmappedCount
                        : key === "agents"
                          ? activeInstallations.length
                          : 0;
              const active = tab === key;
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={`att-machines-nav__btn${active ? " is-active" : ""}`}
                  onClick={() => setTab(key)}
                >
                  <span className="att-machines-nav__icon" aria-hidden>
                    <i className={`bi ${icon}`} />
                  </span>
                  <span className="att-machines-nav__text">
                    <span className="att-machines-nav__label">{t(`app.attendance.machines.tab.${key}`)}</span>
                    {count > 0 ? <span className="att-machines-nav__count">{count}</span> : null}
                  </span>
                </button>
              );
            })}
          </div>

          {loading ? (
            <p>{t("app.common.loading")}</p>
          ) : tab === "agents" ? (
            <section className="att-config-panel att-machines-unmapped att-machines-agents">
              <div className="att-config-panel-head att-machines-agents__head mb-3">
                <div className="att-config-icon-badge att-config-icon-badge--primary" aria-hidden>
                  <i className="bi bi-pc-display" />
                </div>
                <div className="att-config-panel-head-text">
                  <h2 className="att-config-panel-title">{t("app.attendance.machines.setupTitle")}</h2>
                  <p className="att-config-panel-lead mb-0">{t("app.attendance.machines.setupLead")}</p>
                </div>
                <div className="att-machines-agents__actions">
                  <a
                    className="att-btn att-btn--save-primary att-machines-download"
                    href={ATTENDANCE_AGENT_DOWNLOAD_URL}
                    rel="noopener noreferrer"
                    download
                  >
                    <i className="bi bi-download" aria-hidden />
                    {t("app.attendance.machines.downloadAgent")}
                  </a>
                </div>
              </div>

              <ol className="att-machines-setup">
                <li>{t("app.attendance.machines.setupStep1")}</li>
                <li>{t("app.attendance.machines.setupStep2")}</li>
              </ol>

              <div className="att-machines-guide">
                <h3 className="att-machines-guide__title">{t("app.attendance.machines.guideTitle")}</h3>
                <p className="att-machines-guide__lead">{t("app.attendance.machines.guideLead")}</p>
                <div className="att-machines-guide__grid">
                  {AGENT_GUIDE_STEPS.map((step) => (
                    <article key={step.titleKey} className="att-machines-guide__card">
                      <h4 className="att-machines-guide__card-title">
                        {t(`app.attendance.machines.${step.titleKey}`)}
                      </h4>
                      <p className="att-machines-guide__card-body">
                        {t(`app.attendance.machines.${step.bodyKey}`)}
                      </p>
                      <div className="att-machines-guide__img-wrap">
                        <img
                          src={step.image}
                          alt={t(`app.attendance.machines.${step.altKey}`)}
                          loading="lazy"
                        />
                      </div>
                    </article>
                  ))}
                </div>
              </div>

              <h3 className="att-machines-section-title">{t("app.attendance.machines.activeAgents")}</h3>
              {activeInstallations.length === 0 ? (
                <p className="text-muted small">{t("app.attendance.machines.noAgents")}</p>
              ) : (
                <div className="att-machines-grid-card mb-4">
                  <TableContainer className="att-machines-grid-scroll">
                    <table className="data-table att-machines-grid">
                      <thead>
                        <tr>
                          <th className="att-machines-grid__col-device">{t("app.attendance.machines.colName")}</th>
                          <th className="att-machines-grid__col-enroll">{t("app.attendance.machines.colVersion")}</th>
                          <th className="att-machines-grid__col-time">{t("app.attendance.machines.colLastSeen")}</th>
                          <th className="att-machines-grid__col-time">{t("app.attendance.machines.colCreated")}</th>
                          <th className="att-machines-grid__col-actions">{t("app.attendance.machines.colActions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeInstallations.map((row) => (
                          <tr key={row.id}>
                            <td className="att-machines-grid__col-device">
                              <span className="att-machines-when__date">{row.name || row.id}</span>
                            </td>
                            <td className="att-machines-grid__col-enroll">
                              <span className="att-machines-enroll">{row.agentVersion || "—"}</span>
                            </td>
                            <td className="att-machines-grid__col-time">{formatWhen(row.lastSeenAt)}</td>
                            <td className="att-machines-grid__col-time">{formatWhen(row.createdDate)}</td>
                            <td className="att-machines-grid__col-actions">
                              <button
                                type="button"
                                className="att-btn att-btn--ghost btn-sm"
                                disabled={busy}
                                onClick={() => onRevoke(row.id, row.name)}
                              >
                                {t("app.attendance.machines.revoke")}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableContainer>
                </div>
              )}

              {revokedInstallations.length > 0 ? (
                <>
                  <h3 className="att-machines-section-title att-machines-section-title--muted">
                    {t("app.attendance.machines.revokedAgents")}
                  </h3>
                  <div className="att-machines-grid-card">
                    <TableContainer className="att-machines-grid-scroll">
                      <table className="data-table att-machines-grid">
                        <thead>
                          <tr>
                            <th className="att-machines-grid__col-device">{t("app.attendance.machines.colName")}</th>
                            <th className="att-machines-grid__col-enroll">{t("app.attendance.machines.colVersion")}</th>
                            <th className="att-machines-grid__col-time">{t("app.attendance.machines.colLastSeen")}</th>
                            <th className="att-machines-grid__col-time">{t("app.attendance.machines.colCreated")}</th>
                            <th className="att-machines-grid__col-dir">{t("app.attendance.machines.colStatus")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {revokedInstallations.map((row) => (
                            <tr key={row.id}>
                              <td className="att-machines-grid__col-device">
                                <span className="att-machines-when__date">{row.name || row.id}</span>
                              </td>
                              <td className="att-machines-grid__col-enroll">{row.agentVersion || "—"}</td>
                              <td className="att-machines-grid__col-time">{formatWhen(row.lastSeenAt)}</td>
                              <td className="att-machines-grid__col-time">{formatWhen(row.createdDate)}</td>
                              <td className="att-machines-grid__col-dir">
                                <span className="att-machines-hint att-machines-hint--out">
                                  {t("app.attendance.machines.statusRevoked")}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </TableContainer>
                  </div>
                </>
              ) : null}
            </section>
          ) : tab === "devices" ? (
            <section className="att-config-panel att-machines-unmapped">
              <div className="att-config-panel-head mb-3">
                <div className="att-config-icon-badge att-config-icon-badge--primary" aria-hidden>
                  <i className="bi bi-hdd-network" />
                </div>
                <div className="att-config-panel-head-text">
                  <h2 className="att-config-panel-title">{t("app.attendance.machines.devicesTitle")}</h2>
                  <p className="att-config-panel-lead mb-0">{t("app.attendance.machines.devicesLead")}</p>
                </div>
              </div>

              {devices.length === 0 ? (
                <p className="text-muted small mb-0">{t("app.attendance.machines.noDevices")}</p>
              ) : (
                <div className="att-machines-grid-card">
                  <TableContainer className="att-machines-grid-scroll">
                    <table className="data-table att-machines-grid">
                      <thead>
                        <tr>
                          <th className="att-machines-grid__col-device">{t("app.attendance.machines.colName")}</th>
                          <th className="att-machines-grid__col-time">{t("app.attendance.machines.colOffice")}</th>
                          <th className="att-machines-grid__col-enroll">{t("app.attendance.machines.colStatus")}</th>
                          <th className="att-machines-grid__col-time">{t("app.attendance.machines.colIp")}</th>
                          <th className="att-machines-grid__col-time">{t("app.attendance.machines.colLastSeen")}</th>
                          <th className="att-machines-grid__col-note">{t("app.attendance.machines.error")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {devices.map((d) => {
                          const status = deviceStatusMeta(d, t);
                          const seen = formatWhen(d.lastHeartbeatAt || d.lastSyncAt);
                          const endpoint = d.ipAddress ? `${d.ipAddress}${d.port ? `:${d.port}` : ""}` : "—";
                          return (
                            <tr key={d.id}>
                              <td className="att-machines-grid__col-device">
                                <div className="att-machines-when">
                                  <span className="att-machines-when__date">{d.name || "—"}</span>
                                  <span className="att-machines-when__time">{d.vendor || d.externalDeviceKey}</span>
                                </div>
                              </td>
                              <td className="att-machines-grid__col-time">
                                {d.officeName || (d.officeId ? `#${d.officeId}` : "—")}
                              </td>
                              <td className="att-machines-grid__col-dir">
                                <span className={status.className}>{status.label}</span>
                              </td>
                              <td className="att-machines-grid__col-time">
                                <span className="att-machines-device">{endpoint}</span>
                              </td>
                              <td className="att-machines-grid__col-time">{seen}</td>
                              <td className="att-machines-grid__col-note">
                                <span className="att-machines-note" title={d.lastError || undefined}>
                                  {d.lastError || "—"}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </TableContainer>
                </div>
              )}
            </section>
          ) : tab === "mappings" ? (
            <section className="att-config-panel att-machines-unmapped att-machines-map">
              <div className="att-config-panel-head mb-3">
                <div className="att-config-icon-badge att-config-icon-badge--success" aria-hidden>
                  <i className="bi bi-person-badge" />
                </div>
                <div className="att-config-panel-head-text">
                  <h2 className="att-config-panel-title">{t("app.attendance.machines.mappingsTitle")}</h2>
                  <p className="att-config-panel-lead mb-0">{t("app.attendance.machines.mappingsLead")}</p>
                </div>
              </div>

              <form className="att-map-leftover" onSubmit={onSaveLeftoverMapping}>
                <div className="att-map-leftover__head">
                  <h3 className="att-map-leftover__title">{t("app.attendance.machines.leftoverTitle")}</h3>
                  <p className="att-map-leftover__lead">{t("app.attendance.machines.leftoverLead")}</p>
                </div>
                <div className="att-map-leftover__fields">
                  <label className="att-map-field">
                    <span>{t("app.attendance.machines.enrollmentNo")}</span>
                    <MappingEnrollEditor
                      currentEnroll=""
                      draft={leftoverDraft}
                      enrollOptions={leftoverEnrollList}
                      t={t}
                      disabled={busy}
                      compact={false}
                      onChange={(next) => {
                        setLeftoverEnroll(next.select);
                        setLeftoverCustom(next.custom || "");
                      }}
                    />
                  </label>
                  <label className="att-map-field">
                    <span>{t("app.attendance.machines.employee")}</span>
                    <select
                      className="form-select"
                      value={leftoverUserId}
                      disabled={busy}
                      onChange={(e) => setLeftoverUserId(e.target.value)}
                    >
                      <option value="">{t("app.attendance.machines.employeeSelectPlaceholder")}</option>
                      {unmappedEmployees.map((emp) => (
                        <option key={emp.userId} value={emp.userId}>
                          {emp.userName}
                          {emp.employeeCode ? ` (${emp.employeeCode})` : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="att-map-field">
                    <span>{t("app.attendance.machines.deviceOptional")}</span>
                    <select
                      className="form-select"
                      value={leftoverDeviceId}
                      disabled={busy}
                      onChange={(e) => setLeftoverDeviceId(e.target.value)}
                    >
                      <option value="">{t("app.attendance.machines.anyDevice")}</option>
                      {devices.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name || d.externalDeviceKey}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="att-map-leftover__action">
                    <button type="submit" className="att-btn att-btn--save-primary" disabled={busy}>
                      {t("app.attendance.machines.addMapping")}
                    </button>
                  </div>
                </div>
              </form>

              <div className="att-map-filters" role="search">
                <label className="att-map-search">
                  <i className="bi bi-search" aria-hidden />
                  <input
                    type="search"
                    className="form-control"
                    value={mapSearchInput}
                    placeholder={t("app.attendance.machines.searchPlaceholder")}
                    onChange={(e) => setMapSearchInput(e.target.value)}
                  />
                </label>
                <div className="att-map-status" role="tablist" aria-label={t("app.attendance.machines.filterStatus")}>
                  {[
                    { key: "unmapped", label: t("app.attendance.machines.filterUnmapped"), count: rosterUnmappedCount },
                    { key: "mapped", label: t("app.attendance.machines.filterMapped"), count: rosterMappedCount },
                    { key: "all", label: t("app.attendance.machines.filterAllEmployees"), count: rosterUnmappedCount + rosterMappedCount },
                  ].map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      role="tab"
                      aria-selected={mapStatus === item.key}
                      className={`att-map-status__btn${mapStatus === item.key ? " is-active" : ""}`}
                      onClick={() => setMapFilter(item.key)}
                    >
                      {item.label}
                      <span className="att-map-status__count">{item.count}</span>
                    </button>
                  ))}
                </div>
              </div>

              {rosterTotal === 0 && !rosterLoading ? (
                <p className="text-muted small mb-0">
                  {mapStatus === "unmapped"
                    ? t("app.attendance.machines.noUnmappedEmployees")
                    : t("app.attendance.machines.mappingRosterEmpty")}
                </p>
              ) : (
                <div className={`att-machines-grid-card${rosterLoading ? " is-loading" : ""}`}>
                  <TableContainer className="att-machines-grid-scroll">
                    <table className="data-table att-machines-grid att-map-grid">
                      <thead>
                        <tr>
                          <th className="att-map-grid__col-employee">{t("app.attendance.machines.employee")}</th>
                          <th className="att-map-grid__col-email">{t("app.attendance.machines.colEmail")}</th>
                          <th className="att-map-grid__col-designation">{t("app.attendance.machines.colDesignation")}</th>
                          <th className="att-map-grid__col-department">{t("app.attendance.machines.colDepartment")}</th>
                          <th className="att-map-grid__col-office">{t("app.attendance.machines.colOffice")}</th>
                          <th className="att-map-grid__col-status">{t("app.attendance.machines.colStatus")}</th>
                          <th className="att-map-grid__col-enroll">{t("app.attendance.machines.enrollmentNo")}</th>
                          <th className="att-map-grid__col-actions">{t("app.attendance.machines.colActions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rosterItems.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="att-machines-grid__empty">
                              {t("app.attendance.machines.mappingRosterEmpty")}
                            </td>
                          </tr>
                        ) : (
                          rosterItems.map((row) => {
                            const draft = enrollDrafts[row.userId];
                            const nextEnroll = resolveEnrollDraft(row.enrollmentNo, draft, enrollOptions);
                            const dirty = nextEnroll !== String(row.enrollmentNo || "").trim();
                            return (
                              <tr key={row.userId}>
                                <td className="att-map-grid__col-employee">
                                  <div className="att-cr-queue-person att-map-person">
                                    <div className="att-cr-queue-avatar">
                                      <MappingEmployeeAvatar url={row.imageUrl} name={row.userName} />
                                    </div>
                                    <div className="att-cr-queue-person__text">
                                      <div className="att-cr-queue-person__name">{row.userName || "—"}</div>
                                      {row.employeeCode ? (
                                        <div className="att-cr-queue-person__meta small">
                                          {t("app.attendance.machines.employeeCode")}: {row.employeeCode}
                                        </div>
                                      ) : null}
                                    </div>
                                  </div>
                                </td>
                                <td className="att-map-grid__col-email">
                                  <span className="att-map-grid__email" title={row.email || undefined}>
                                    {row.email || "—"}
                                  </span>
                                </td>
                                <td className="att-map-grid__col-designation">{row.designation || "—"}</td>
                                <td className="att-map-grid__col-department">{row.departmentName || "—"}</td>
                                <td className="att-map-grid__col-office">{row.officeName || "—"}</td>
                                <td className="att-map-grid__col-status">
                                  <span
                                    className={
                                      row.isMapped
                                        ? "att-machines-hint att-machines-hint--in"
                                        : "att-machines-hint att-machines-hint--out"
                                    }
                                  >
                                    {row.isMapped
                                      ? t("app.attendance.machines.mappedBadge")
                                      : t("app.attendance.machines.unmappedBadge")}
                                  </span>
                                </td>
                                <td className="att-map-grid__col-enroll">
                                  <MappingEnrollEditor
                                    currentEnroll={row.enrollmentNo}
                                    draft={draft}
                                    enrollOptions={enrollOptions}
                                    t={t}
                                    disabled={busy}
                                    onChange={(next) =>
                                      setEnrollDrafts((prev) => ({ ...prev, [row.userId]: next }))
                                    }
                                  />
                                </td>
                                <td className="att-map-grid__col-actions">
                                  <button
                                    type="button"
                                    className="att-btn att-btn--save-primary btn-sm"
                                    disabled={busy || !nextEnroll || (!dirty && row.isMapped)}
                                    onClick={() => onSaveRowEnrollment(row)}
                                  >
                                    {t("app.attendance.machines.saveEnroll")}
                                  </button>
                                  {row.isMapped && row.mappingId ? (
                                    <button
                                      type="button"
                                      className="att-btn att-btn--ghost btn-sm"
                                      disabled={busy}
                                      onClick={() =>
                                        setMappingToRemove({
                                          mappingId: row.mappingId,
                                          enrollmentNo: row.enrollmentNo,
                                          userName: row.userName,
                                        })
                                      }
                                    >
                                      {t("app.attendance.machines.removeMapping")}
                                    </button>
                                  ) : null}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </TableContainer>
                  {rosterTotal > 0 ? (
                    <div
                      className={`documents-pagination documents-pagination--grid-pager${rosterLoading ? " is-loading" : ""}`}
                      aria-busy={rosterLoading}
                      aria-label={t("app.attendance.machines.mappingPaginationAria")}
                    >
                      <span className="documents-pagination-total">
                        {t("app.attendance.correctionsQueue.paginationRange", {
                          from: rosterFrom,
                          to: rosterTo,
                          total: rosterTotal,
                        })}
                      </span>
                      <div className="documents-pagination-pager-wrap">
                        <GridPager
                          className="documents-rc-pagination"
                          onChange={handleRosterPageChange}
                          current={rosterPage}
                          total={rosterTotal}
                          pageSize={rosterPageSize}
                          defaultPageSize={MAPPING_ROSTER_PAGE_SIZE}
                          showLessItems
                          showTitle={false}
                          showSizeChanger={false}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </section>
          ) : tab === "mapped" ? (
            <section className="att-config-panel att-machines-unmapped">
              <div className="att-config-panel-head mb-3">
                <div className="att-config-icon-badge att-config-icon-badge--success" aria-hidden>
                  <i className="bi bi-check2-circle" />
                </div>
                <div className="att-config-panel-head-text">
                  <h2 className="att-config-panel-title">{t("app.attendance.machines.mappedTitle")}</h2>
                  <p className="att-config-panel-lead mb-0">{t("app.attendance.machines.mappedLead")}</p>
                </div>
              </div>

              {mappedTotal === 0 && !mappedLoading ? (
                <p className="text-muted small mb-0">{t("app.attendance.machines.noMapped")}</p>
              ) : (
                <div className={`att-machines-grid-card${mappedLoading ? " is-loading" : ""}`}>
                  <TableContainer className="att-machines-grid-scroll">
                    <table className="data-table att-machines-grid">
                      <thead>
                        <tr>
                          <th className="att-map-grid__col-employee">{t("app.attendance.machines.employee")}</th>
                          <th className="att-machines-grid__col-enroll">{t("app.attendance.machines.enrollmentNo")}</th>
                          <th className="att-machines-grid__col-device">{t("app.attendance.machines.deviceKey")}</th>
                          <th className="att-machines-grid__col-time">{t("app.attendance.machines.punchTime")}</th>
                          <th className="att-machines-grid__col-dir">{t("app.attendance.machines.direction")}</th>
                          <th className="att-machines-grid__col-enroll">{t("app.attendance.machines.colStatus")}</th>
                          <th className="att-machines-grid__col-note">{t("app.attendance.machines.error")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mapped.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="att-machines-grid__empty">
                              {t("app.attendance.machines.noMapped")}
                            </td>
                          </tr>
                        ) : (
                          mapped.map((p) => {
                            const when = formatPunchWhen(p.punchTimeUtc);
                            const hint = mappedPunchDirection(p, t);
                            const status = mappedPunchStatusMeta(p.processStatus, t);
                            return (
                              <tr key={p.id}>
                                <td className="att-map-grid__col-employee">{p.userName || "—"}</td>
                                <td className="att-machines-grid__col-enroll">
                                  <span className="att-machines-enroll">{p.enrollmentNo || "—"}</span>
                                </td>
                                <td className="att-machines-grid__col-device">
                                  <span className="att-machines-device">{p.externalDeviceKey || "—"}</span>
                                </td>
                                <td className="att-machines-grid__col-time">
                                  <div className="att-machines-when">
                                    <span className="att-machines-when__date">{when.date}</span>
                                    <span className="att-machines-when__time">{when.time}</span>
                                  </div>
                                </td>
                                <td className="att-machines-grid__col-dir">
                                  <span className={hint.className}>{hint.label}</span>
                                </td>
                                <td className="att-machines-grid__col-dir">
                                  <span className={status.className}>{status.label}</span>
                                </td>
                                <td className="att-machines-grid__col-note">
                                  <span className="att-machines-note" title={p.processError || undefined}>
                                    {p.processError || "—"}
                                  </span>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </TableContainer>
                  {mappedTotal > 0 ? (
                    <div
                      className={`documents-pagination documents-pagination--grid-pager${mappedLoading ? " is-loading" : ""}`}
                      aria-busy={mappedLoading}
                      aria-label={t("app.attendance.machines.mappedPaginationAria")}
                    >
                      <span className="documents-pagination-total">
                        {t("app.attendance.correctionsQueue.paginationRange", {
                          from: mappedFrom,
                          to: mappedTo,
                          total: mappedTotal,
                        })}
                      </span>
                      <div className="documents-pagination-pager-wrap">
                        <GridPager
                          className="documents-rc-pagination"
                          onChange={handleMappedPageChange}
                          current={mappedPage}
                          total={mappedTotal}
                          pageSize={mappedPageSize}
                          defaultPageSize={UNMAPPED_DEFAULT_PAGE_SIZE}
                          showLessItems
                          showTitle={false}
                          showSizeChanger={false}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </section>
          ) : (
            <section className="att-config-panel att-machines-unmapped">
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
                <div className="att-config-panel-head mb-0">
                  <div className="att-config-icon-badge att-config-icon-badge--primary" aria-hidden>
                    <i className="bi bi-exclamation-diamond" />
                  </div>
                  <div className="att-config-panel-head-text">
                    <h2 className="att-config-panel-title">{t("app.attendance.machines.unmappedTitle")}</h2>
                    <p className="att-config-panel-lead mb-0">{t("app.attendance.machines.unmappedLead")}</p>
                  </div>
                </div>
                <button
                  type="button"
                  className="att-btn att-btn--save-primary"
                  disabled={busy || unmappedTotal === 0}
                  onClick={() => setReprocessOpen(true)}
                >
                  {t("app.attendance.machines.reprocess")}
                </button>
              </div>

              {unmappedTotal === 0 && !unmappedLoading ? (
                <p className="text-muted small mb-0">{t("app.attendance.machines.noUnmapped")}</p>
              ) : (
                <div className={`att-machines-grid-card${unmappedLoading ? " is-loading" : ""}`}>
                  <TableContainer className="att-machines-grid-scroll">
                    <table className="data-table att-machines-grid">
                      <thead>
                        <tr>
                          <th className="att-machines-grid__col-enroll">{t("app.attendance.machines.enrollmentNo")}</th>
                          <th className="att-machines-grid__col-device">{t("app.attendance.machines.deviceKey")}</th>
                          <th className="att-machines-grid__col-time">{t("app.attendance.machines.punchTime")}</th>
                          <th className="att-machines-grid__col-dir">{t("app.attendance.machines.direction")}</th>
                          <th className="att-machines-grid__col-note">{t("app.attendance.machines.error")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {unmapped.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="att-machines-grid__empty">
                              {t("app.attendance.machines.noUnmapped")}
                            </td>
                          </tr>
                        ) : (
                          unmapped.map((p) => {
                            const when = formatPunchWhen(p.punchTimeUtc);
                            const hint = inOutHintMeta(p.inOutHint, t);
                            return (
                              <tr key={p.id}>
                                <td className="att-machines-grid__col-enroll">
                                  <span className="att-machines-enroll">{p.enrollmentNo || "—"}</span>
                                </td>
                                <td className="att-machines-grid__col-device">
                                  <span className="att-machines-device">{p.externalDeviceKey || "—"}</span>
                                </td>
                                <td className="att-machines-grid__col-time">
                                  <div className="att-machines-when">
                                    <span className="att-machines-when__date">{when.date}</span>
                                    <span className="att-machines-when__time">{when.time}</span>
                                  </div>
                                </td>
                                <td className="att-machines-grid__col-dir">
                                  <span className={hint.className}>{hint.label}</span>
                                </td>
                                <td className="att-machines-grid__col-note">
                                  <span className="att-machines-note" title={p.processError || undefined}>
                                    {p.processError || "—"}
                                  </span>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </TableContainer>
                  {unmappedTotal > 0 ? (
                    <div
                      className={`documents-pagination documents-pagination--grid-pager${unmappedLoading ? " is-loading" : ""}`}
                      aria-busy={unmappedLoading}
                      aria-label={t("app.attendance.machines.paginationAria")}
                    >
                      <span className="documents-pagination-total">
                        {t("app.attendance.correctionsQueue.paginationRange", {
                          from: unmappedFrom,
                          to: unmappedTo,
                          total: unmappedTotal,
                        })}
                      </span>
                      <div className="documents-pagination-pager-wrap">
                        <GridPager
                          className="documents-rc-pagination"
                          onChange={handleUnmappedPageChange}
                          current={unmappedPage}
                          total={unmappedTotal}
                          pageSize={unmappedPageSize}
                          defaultPageSize={UNMAPPED_DEFAULT_PAGE_SIZE}
                          showLessItems
                          showTitle={false}
                          showSizeChanger={false}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </section>
          )}
        </div>
      </div>

      {mappingToRemove ? (
        <div
          className="att-modal-overlay"
          role="presentation"
          onMouseDown={(e) => e.target === e.currentTarget && closeRemoveMapping()}
        >
          <div
            className="att-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="att-map-remove-title"
            style={{ maxWidth: 420 }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="att-modal-header">
              <h2 className="att-modal-title mb-0" id="att-map-remove-title">
                {t("app.attendance.machines.mappingDeleteTitle")}
              </h2>
              <button
                type="button"
                className="att-modal-close"
                aria-label={t("app.common.close")}
                disabled={busy}
                onClick={closeRemoveMapping}
              >
                ×
              </button>
            </div>
            <div className="att-modal-body">
              <p className="mb-0 text-muted small">
                {t("app.attendance.machines.mappingDeleteConfirm", {
                  enroll: mappingToRemove.enrollmentNo || mappingToRemove.mappingId,
                })}
              </p>
              {mappingToRemove.userName ? (
                <p className="mb-0 mt-2 fw-semibold">{mappingToRemove.userName}</p>
              ) : null}
            </div>
            <div className="att-modal-footer">
              <button type="button" className="att-btn att-btn--ghost" disabled={busy} onClick={closeRemoveMapping}>
                {t("app.common.cancel")}
              </button>
              <button type="button" className="btn btn-outline-danger" disabled={busy} onClick={confirmRemoveMapping}>
                {t("app.attendance.machines.removeMapping")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {reprocessOpen ? (
        <div
          className="att-modal-overlay"
          role="presentation"
          onMouseDown={(e) => e.target === e.currentTarget && closeReprocess()}
        >
          <div
            className="att-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="att-unmapped-process-title"
            style={{ maxWidth: 460 }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="att-modal-header">
              <h2 className="att-modal-title mb-0" id="att-unmapped-process-title">
                {t("app.attendance.machines.reprocessTitle")}
              </h2>
              <button
                type="button"
                className="att-modal-close"
                aria-label={t("app.common.close")}
                disabled={busy}
                onClick={closeReprocess}
              >
                ×
              </button>
            </div>
            <div className="att-modal-body">
              <p className="att-modal-warning">
                {t("app.attendance.machines.reprocessWarning")}
              </p>
              <p className="mb-0 text-muted small">
                {t("app.attendance.machines.reprocessLead", { count: unmappedTotal })}
              </p>
            </div>
            <div className="att-modal-footer">
              <button type="button" className="att-btn att-btn--ghost" disabled={busy} onClick={closeReprocess}>
                {t("app.common.cancel")}
              </button>
              <button type="button" className="att-btn att-btn--save-primary" disabled={busy} onClick={onReprocess}>
                {busy ? t("app.attendance.machines.reprocessBusy") : t("app.attendance.machines.reprocessConfirm")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AttendanceMachinesPage;
